import { Helmet } from 'react-helmet-async';
import ContactUsComponent from '../../components/ContactUsComponent'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAddressBook,
  faUsers,
  faChartLine,
  faComments,
  faListCheck,
  faLayerGroup,
  faEye,
  faChartSimple,
  faCheckCircle,
  faClock,
  faGlobe
} from '@fortawesome/free-solid-svg-icons'

const technologiesIcon = [
  "../images/react.png",
  "../images/azure.svg",
  "../images/azure-sql.png",
  "../images/nodejs.png",
  "../images/csharp.png",
];

const Crm = () => {
  return (
    <>
      <Helmet>
        <title>JV CRM Case Study | CRM Software by Data Repo</title>
        <meta
          name="description"
          content="Learn how Data Repo developed JV CRM, a customer relationship management system that helps businesses manage leads, track customer interactions, and improve sales performance."
        />
      </Helmet>

      <section
        style={{
          backgroundImage: `url(../images/bg-casestudies-box.png)`,
          backgroundSize: "100% 80vh"
        }}
        className="relative flex flex-col justify-center w-full h-[65vh] bg-no-repeat bg-fixed pt-[65px] text-center text-white">
        <h1 className="text-3xl md:text-6xl font-bold uppercase">JV CRM</h1>
        <p className="mt-6 text-lg md:text-2xl max-w-3xl mx-auto">
          Customer Relationship Management Software by Data Repo
        </p>
      </section>

      <section className="bg-gray-100 py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-6">Strengthening Customer Relationships</h2>
            <p className="text-lg text-gray-700 mb-4">
              Businesses often struggle to track customer interactions, manage leads, and
              maintain consistent communication across their sales teams.
            </p>
            <p className="text-lg text-gray-700">
              JV CRM was developed by Data Repo to provide a centralized platform that helps
              businesses manage customer data, monitor sales pipelines, and improve engagement.
            </p>
          </div>
          <img src="../images/jvcrm-dashboard.png" alt="CRM dashboard overview" className="rounded-xl shadow-lg" />
        </div>
      </section>

      <section className="bg-white py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto text-center mb-10">
          <h2 className="text-3xl font-bold mb-4">Inside the JV CRM Dashboard</h2>
          <p className="text-gray-600 max-w-3xl mx-auto">
            JV CRM provides a clear view of leads, customer interactions,
            and sales performance in one unified platform.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <img src="../images/jvcrm-deals.png" className="rounded-xl shadow-lg" alt="jv crm" />
          <img src="../images/jvcrm-tickets.png" className="rounded-xl shadow-lg" alt="jv crm" />
        </div>
      </section>

      <section className="bg-gradient-to-r from-blue-50 to-white py-8 px-6 md:px-20 border-y">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">

          <div className="flex items-center gap-4 justify-center md:justify-start">
            <div className="bg-blue-100 text-blue-700 p-3 rounded-full text-xl">
              <FontAwesomeIcon icon={faGlobe} />
            </div>
            <div>
              <p className="text-sm uppercase tracking-wide text-gray-500">Live Product</p>
              <p className="text-lg font-semibold text-gray-800">
                JV CRM helps businesses manage leads and customer relationships more effectively
              </p>
            </div>
          </div>

          <a
            href="https://jvcrm.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-blue-800 transition"
          >
            Visit Website →
          </a>
        </div>
      </section>

      <section className="bg-white py-20 px-6 md:px-20">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">Project Objectives</h2>
          <p className="text-gray-600 text-lg mb-14 max-w-3xl mx-auto">
            JV CRM was built to improve lead management, enhance customer communication,
            and provide better visibility into sales performance.
          </p>

          <div className="grid md:grid-cols-3 gap-10">
            <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faLayerGroup} className="text-3xl text-blue-700 mb-4" />
              <h3 className="font-semibold text-xl mb-2">Centralized Customer Data</h3>
              <p>Store all customer interactions and contact details in one place.</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faEye} className="text-3xl text-blue-700 mb-4" />
              <h3 className="font-semibold text-xl mb-2">Sales Pipeline Visibility</h3>
              <p>Give teams clear insight into leads and deal progress.</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faChartSimple} className="text-3xl text-blue-700 mb-4" />
              <h3 className="font-semibold text-xl mb-2">Scalable CRM Platform</h3>
              <p>Support growing sales teams and expanding customer bases.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-100 py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <img src="../images/event-manual.jpg" alt="Manual CRM tracking" className="rounded-xl shadow-md" />
          <div>
            <h2 className="text-3xl font-bold mb-6">CRM Challenges Businesses Faced</h2>
            <ul className="list-disc pl-5 text-lg text-gray-700 space-y-3">
              <li>Customer data scattered across spreadsheets and emails</li>
              <li>Difficulty tracking lead progress</li>
              <li>Lack of visibility into sales performance</li>
              <li>Inconsistent communication between teams and clients</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 px-6 md:px-20">
        <h2 className="text-3xl font-bold text-center mb-12">Core Software Modules</h2>

        <div className="grid md:grid-cols-3 gap-12 text-center">
          <div>
            <FontAwesomeIcon icon={faAddressBook} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Contact Management</h3>
            <p>Store and organize customer and lead information efficiently.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faListCheck} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Lead & Deal Tracking</h3>
            <p>Track lead status and manage deals through the sales pipeline.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faComments} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Communication Logs</h3>
            <p>Record calls, emails, and interactions with customers.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faUsers} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Team Collaboration</h3>
            <p>Allow teams to share updates and coordinate sales efforts.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faChartLine} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Sales Reports</h3>
            <p>Generate performance reports to analyze sales activity.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faLayerGroup} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Multi-Team Support</h3>
            <p>Manage multiple sales teams within one CRM platform.</p>
          </div>
        </div>
      </section>

      <section className="bg-gray-100 py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto text-center mb-10">
          <h2 className="text-3xl font-bold mb-4">Key Features in Action</h2>
          <p className="text-gray-600 max-w-3xl mx-auto">
            Manage contacts, track deals, monitor sales performance,
            and maintain customer communication history.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <img src="../images/jvcrm-calls.png" className="rounded-xl shadow-lg" alt="jv crm" />
          <img src="../images/jvcrm-meetings.png" className="rounded-xl shadow-lg" alt="jv crm" />
          <img src="../images/jvcrm-access-control.png" className="rounded-xl shadow-lg" alt="jv crm" />
          <img src="../images/jvcrm-company-settings.png" className="rounded-xl shadow-lg" alt="jv crm" />
        </div>
      </section>

      <section className="bg-gradient-to-b from-gray-100 to-white py-20 px-6 md:px-20 text-center">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-4">Business Impact</h2>
          <p className="text-gray-600 text-lg mb-14 max-w-3xl mx-auto">
            Companies using JV CRM improved lead tracking, enhanced communication,
            and gained clearer visibility into their overall sales performance.
          </p>

          <div className="grid md:grid-cols-3 gap-10">
            <div className="bg-white p-10 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faCheckCircle} className="text-3xl text-green-600 mb-4" />
              <h3 className="font-semibold text-lg">Better Lead Management</h3>
            </div>
            <div className="bg-white p-10 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faClock} className="text-3xl text-blue-600 mb-4" />
              <h3 className="font-semibold text-lg">Faster Follow-Ups</h3>
            </div>
            <div className="bg-white p-10 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faChartLine} className="text-3xl text-yellow-600 mb-4" />
              <h3 className="font-semibold text-lg">Improved Sales Insights</h3>
            </div>
          </div>
        </div>
      </section>

      <section className="flex flex-col items-center py-16 px-8">
        <h2 className="text-4xl font-bold mb-8">Technologies Used</h2>
        <div className="flex flex-wrap gap-8 justify-center">
          {technologiesIcon.map((item, index) => (
            <img key={index} src={item} alt="technology" width="90" />
          ))}
        </div>
      </section>

      <ContactUsComponent />
    </>
  )
}

export default Crm
